import { Box, Typography } from '@mui/material'
import { ClickableArea } from '@/objects/property-forms/ClickableArea.tsx'
import { useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { useOptionsPopover } from '@/core/popover/popover-hooks.tsx'
import { DefaultPopover } from '@/core/popover/DefaultPopover.tsx'
import { MultiSelectPropertyValuePopover } from 'objects/property-forms/multi-select/MultiSelectPropertyValuePopover.tsx'
import { MultiSelectOptionChip } from 'objects/property-forms/multi-select/MultiSelectOptionChip.tsx'
import { PropertyOptionEditPopover } from '@/objects/property-forms/PropertyOptionEditPopover.tsx'
import { withoutArr } from '@/common/collection-utils.ts'
import { isClientId } from '@/common/network/gql-types.ts'
import { useDiRef } from '@/common/react/di-hooks.ts'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'
import type { PropertyDefinitionModel } from '@/core/gql/gql-models-override.ts'
import type { PropertyOptionModel } from '@/generated/graphql.ts'
import type { PropertyInputProps } from '@/objects/property-forms/PropertyInputProps.tsx'
import { globalCssVars } from '@/core/theme/GlobalCssVars.ts'
import { propertyFormConfig } from '@/objects/property-forms/PropertyFormConfig.tsx'

export interface MultiSelectPropertyInputProps extends PropertyInputProps<number[]> {
  options: PropertyOptionModel[]
  /** When provided, enables creating, editing, and deleting options targeting this prop def. */
  editable?: { propDef: PropertyDefinitionModel }
  placeholder?: string
}

const chipGapPx = 4 // theme.spacing(0.5)
const plusNWidthPx = 20

export function MultiSelectPropertyInput({
  options,
  value,
  onValueChange,
  editable,
  placeholder = 'Empty',
}: MultiSelectPropertyInputProps) {
  const typesService = useDiRef<ObjectTypesService>('ObjectTypesService')
  const popover = useOptionsPopover<void>()
  const optionEditPopover = useOptionsPopover<void>()
  const [editedOptionId, setEditedOptionId] = useState<number | undefined>(undefined)
  const editedOption = editedOptionId ? options.find((x) => x.id === editedOptionId) : undefined

  const containerRef = useRef<HTMLDivElement>(null)
  const chipRefs = useRef<(HTMLElement | null)[]>([])
  const badgeRef = useRef<HTMLDivElement>(null)

  const selectedOptions = useMemo(
    () =>
      value
        .map((id) => options.find((o) => o.id === id))
        .filter((x): x is PropertyOptionModel => x != null),
    [value, options],
  )

  const availableOptions =
    selectedOptions.length === 0 ? options : withoutArr(options, selectedOptions)

  useLayoutEffect(() => {
    if (!containerRef.current || !badgeRef.current) return

    const recalc = () => {
      const containerW = containerRef.current!.clientWidth
      const badge = badgeRef.current!
      const chips = chipRefs.current.filter(Boolean) as HTMLElement[]

      // Reset all to visible so we can measure natural widths
      chips.forEach((c) => (c.style.display = ''))
      badge.style.visibility = 'hidden'

      // Reading offsetWidth forces reflow, giving us natural chip widths
      const widths = chips.map((c) => c.offsetWidth)

      let used = 0
      let visibleCount = 0
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] + (i > 0 ? chipGapPx : 0)
        const isLast = i === widths.length - 1
        const spaceNeeded = used + w + (!isLast ? chipGapPx + plusNWidthPx : 0)
        if (spaceNeeded > containerW) break
        used += w
        visibleCount = i + 1
      }

      const hiddenCount = chips.length - visibleCount
      if (hiddenCount > 0) {
        chips.slice(visibleCount).forEach((c) => (c.style.display = 'none'))
        badge.textContent = `+${hiddenCount}`
        badge.style.visibility = 'visible'
      }
    }

    recalc()
    const ro = new ResizeObserver(recalc)
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [selectedOptions])

  function onOptionSelected(option: PropertyOptionModel) {
    if (editable && isClientId(option.id)) {
      void typesService.updatePropertyDef(editable.propDef, {
        options: { locallyCreatedOptions: [option] },
      })
    }
    if (!value.includes(option.id)) {
      onValueChange([...value, option.id])
    }
  }

  function onChipOptionsClick(e: MouseEvent, option: PropertyOptionModel) {
    e.stopPropagation()
    setEditedOptionId(option.id)
    optionEditPopover.openPopover(e.currentTarget)
  }

  function deselectOption(option: PropertyOptionModel) {
    onValueChange(value.filter((id) => id !== option.id))
  }

  function onDeselect(e: MouseEvent, option: PropertyOptionModel) {
    e.stopPropagation()
    deselectOption(option)
  }

  function onEditedOptionChange() {
    if (!editable || !editedOption) return
    void typesService.updatePropertyDef(editable.propDef, {
      options: { updates: [editedOption] },
    })
  }

  function onEditedOptionDelete() {
    if (!editable || !editedOption) return
    void typesService.updatePropertyDef(editable.propDef, {
      options: { toDelete: [editedOption.id] },
    })
  }

  function newOptionFactory(val: string): PropertyOptionModel {
    return typesService.createLocalPropertyOption(editable!.propDef, { value: val })
  }

  return (
    <>
      <ClickableArea onClick={(e) => popover.openPopover(e.currentTarget)}>
        <Box
          ref={containerRef}
          sx={(theme) => ({
            cursor: 'pointer',
            width: '100%',
            minHeight: 32,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            gap: 0.5,
            px: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          })}
        >
          {selectedOptions.length > 0 ? (
            <>
              {selectedOptions.map((opt, i) => (
                <div
                  key={opt.id}
                  ref={(el) => {
                    chipRefs.current[i] = el
                  }}
                  style={{ display: 'inline-flex', flexShrink: 0 }}
                >
                  <MultiSelectOptionChip
                    option={opt}
                    {...(editable && { onOptionsClick: (e) => onChipOptionsClick(e, opt) })}
                    onDeselectClick={(e) => onDeselect(e, opt)}
                  />
                </div>
              ))}
              <Box
                ref={badgeRef}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  height: `${propertyFormConfig.chipHeightPx}px`,
                  minWidth: `${plusNWidthPx}px`,
                  borderRadius: globalCssVars.radius3,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: 'action.selected',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  visibility: 'hidden',
                }}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
          )}
        </Box>
      </ClickableArea>

      <DefaultPopover
        anchorEl={popover.anchorEl}
        close={popover.closePopover}
        slots={{
          popover: {
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          },
        }}
      >
        <MultiSelectPropertyValuePopover
          selectedOptions={selectedOptions}
          availableOptions={availableOptions}
          onOptionSelected={onOptionSelected}
          onChipDeselect={deselectOption}
          {...(editable && { onChipOptionsClick, newOptionFactory })}
        />
      </DefaultPopover>

      {editable && editedOption && (
        <DefaultPopover
          anchorEl={optionEditPopover.anchorEl}
          close={optionEditPopover.closePopover}
        >
          <PropertyOptionEditPopover
            option={editedOption}
            onOptionEdit={onEditedOptionChange}
            onOptionDeleteClicked={onEditedOptionDelete}
          />
        </DefaultPopover>
      )}
    </>
  )
}
